<template>
  <div class="flex flex-col mt-4 pb-0 gap-4">
    <div class="flex flex-col pb-0 gap-4" v-if="isOTPSent">
      <div class="flex flex-col text-center">
        <h5 class="text-md font-semibold mb-2">Verification required</h5>
        <p class="text-base">
          A one-time password has been sent to your mobile number {{ masked }}
          <a @click="reset" class="text-primary font-semibold">Change</a>
        </p>
        <V3ImageComponent src="icons/otp-identifier.png" class="h-10 w-28 self-center mt-4" />
      </div>
      <div class="flex flex-col bg-gray-50 dark:bg-gray-800 pb-8 p-5 items-center">
        <UForm class="space-y-8 w-full py-2">
          <UFormGroup name="otp" label="One Time Password">
            <UInput type="number" v-model="otp" size="lg" placeholder="Enter 6-digit OTP" @keydown="handleOtpInput"
              @wheel="$event.target.blur()" />
          </UFormGroup>
          <div class="flex flex-col items-center">
            <p class="inline-flex items-center text-danger-700 self-start gap-2" v-if="error.length > 0">
              <V3ImageComponent src="icons/danger.png" class="h-3 w-3" />
              <span>{{ error }}</span>
            </p>
            <div class="w-48">
              <UFormGroup>
                <UButton type="button" label="Submit" size="lg" block :loading="isChecking" @click="verifyOTP" />
                <div class="w-full mt-4 text-center">
                  <p class="text-sm text-gray-500" v-if="!resendDisabled">
                    OTP sent Didn't receive?
                    <span class="text-primary font-semibold cursor-pointer" @click="resendOTP"
                      :class="{ 'text-gray-400 cursor-not-allowed': resendDisabled }">
                      Resend OTP
                    </span>
                  </p>
                  <p v-if="resendDisabled" class="text-sm text-gray-500 mt-2">
                    Resend OTP available in {{ timer }} seconds
                  </p>
                </div>
              </UFormGroup>
            </div>
          </div>
        </UForm>
      </div>
    </div>
    <div class="flex flex-col gap-4" v-else>
      <div class="text-center">
        <h5 class="text-md font-semibold mb-2">Let's start with your mobile number</h5>
        <p class="text-base">This helps us to verify your identity</p>
      </div>
      <div class="flex flex-col bg-gray-50 dark:bg-gray-800 pb-8 p-5 items-center">
        <UForm :schema="schema" :state="state" class="space-y-4 w-full" @submit="onSubmit">
          <UFormGroup label="Full Name" name="name">
            <UInput v-model="state.name" size="lg" class="text-sm" placeholder="Enter receiver's full name" @blur="handleNameBlur" />
          </UFormGroup>
          <UFormGroup label="Mobile Number" name="mobile" help="An OTP will be sent to this number for verification">
            <UInput type="number" v-model="state.mobile" size="lg" placeholder="Enter receiver's 10-digit mobile number"
              @keydown="handleMobileInput" @wheel="$event.target.blur()" />
          </UFormGroup>
          <div class="flex flex-col gap-8 items-center">
            <p class="inline-flex items-center text-danger-700 self-start gap-2" v-if="error.length > 0">
              <V3ImageComponent src="icons/danger.png" class="h-3 w-3" />
              <span>{{ error }}</span>
            </p>
            <div class="w-48">
              <UFormGroup>
                <UButton type="submit" label="Get OTP" size="lg" block :loading="isLoading"
                  :disabled="isButtonDisabled" />
              </UFormGroup>
            </div>
            <div class="flex flex-col text-sm" v-if="runtimeConfig?.public?.partnerCode?.toLowerCase() === 'mall4all'">
              <p class="text-center">By proceeding, you agree to the </p>
              <p class="inline-flex text-center gap-1">
                <a href="/about/terms" class="text-primary font-semibold">Terms & Conditions</a>
                <span>and</span>
                <a href="/about/privacy-policy" class="text-primary font-semibold">Privacy Policy</a>
              </p>
            </div>
          </div>
        </UForm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as v from 'valibot';
import type { FormSubmitEvent } from '#ui/types';
const validMobileRegex = /^[6-9]\d{9}$/;
import { useLoginBanner } from '@/composables/useLoginBanner';

const { showWelcomeBanner } = useLoginBanner();

const resendDisabled = ref(false);
const route = useRoute();
const timer = ref(30);
const resendCount = ref(0);
const maxResendAttempts = 2;
const auth = useAuth();
const { gtag } = useGtag();

const schema = v.object({
  // name: v.pipe(v.string(), v.minLength(1, 'The field cannot be left blank')),
  // mobile: v.pipe(
  //   v.any(),
  //   v.transform((input) => input.toString()),
  //   v.minLength(10, 'Provide 10-digit mobile number'),
  //   v.maxLength(10, 'Provide 10-digit mobile number')
  // )
  name: v.optional(v.any()),
  mobile: v.optional(v.any())
});
type Schema = v.InferOutput<typeof schema>;

const state = reactive({
  name: '',
  mobile: ''
});

const handleNameBlur = () => {
  const name = state?.name?.trim();
  const nameRegex = /^[a-zA-Z\s]*$/;

  if (!nameRegex.test(name)) {
    error.value = 'Full name can only contain letters';
  } else if (name.length > 0 && name.length < 3) {
    error.value = 'Full name should contain at least 3 characters';
  } else if (name.length > 40) {
    error.value = 'Full name cannot exceed 40 characters';
  } else {
    error.value = '';
  }
};

const isButtonDisabled = computed(() => {
  const isNameValid = /^[a-zA-Z\s]{3,40}$/.test(state?.name?.trim());
  const isMobileValid = state?.mobile?.toString().trim().length === 10;
  return !(isNameValid && isMobileValid);
});

declare global {
  interface Window {
    BflAutoReadOTP: (otp: string) => void;
  }
}

const BflAutoReadOTP = (receivedOtp: any) => {
  otp.value = receivedOtp;
  // Wait for DOM/reactivity to update before verifying
  setTimeout(() => {
    verifyOTP();
  }, 100);
};

window.BflAutoReadOTP = BflAutoReadOTP;



const handleMobileInput = (event: KeyboardEvent) => {
  const mobileValue = state.mobile.toString().replace(/\D/g, '');
  const validMobileRegex = /^[0-9]{10}$/;
  if (mobileValue.length > 10 && event.key !== "Backspace") {
    event.preventDefault();
    error.value = 'Enter valid 10-digit mobile number'
  } else {
    error.value = '';
  }
};

const isNewUser = async (phone: any) => {
  try {
    const response: any = await useAPIHandler('post', '/auth/userexist', {
      body: { partner: runtimeConfig.public.partnerCode, phone }
    });
    localStorage.setItem('userExists', response.data.Exist);
    return response.data.Exist
  } catch (err) {
    return false;
  }
};


const handleOtpInput = (event: KeyboardEvent) => {
  if (otp.value) {
    const value = otp.value.toString()
    if (value.length > 5 && event.key !== "Backspace") {
      event.preventDefault();
      return;
    }
  }
};

// const sendOTP = async (name: string, phone: string) => {
//   error.value = '';
//   try {
//     const { error } = await useAPIHandler('post', '/auth/otp', {
//       body: { partner: runtimeConfig.public.partnerCode, name, phone }
//     });
//     if (error) {
//       throw new Error();
//     }  
//     isOTPSent.value = true;
//   } catch (err) {
//     error.value = 'Failed to send OTP. Please try after sometime.';
//   } finally {
//     isLoading.value = false;
//   }
// };

const verifyOTP = async () => {
  error.value = '';

  if (otp.value) {
    const value = otp.value.toString();
    if (value.length !== 6) {
      error.value = 'Please provide 6 digits OTP';
      return;
    }
  } else {
    error.value = 'Otp field cannot be empty';
    return;
  }
  isChecking.value = true;
  try {
    const apiResult: any = await useAPIHandler('post', '/auth/otp/verify', {
      body: { partner: runtimeConfig.public.partnerCode, phone: state?.mobile, otp: otp.value }
    });
    const { access_token, user, toast_message, new_user } = apiResult.data

    await auth.setAccessToken(access_token)
    user.id = user?._id
    await auth.setUser(user);
    showWelcomeBanner(
      { first_name: user?.first_name, phone: state?.mobile },
      Boolean(new_user),
      3000,
      {
        toast_variant: new_user ? 'first_time' : 'repeat',
      }
    )
    if(new_user) {
      await navigateTo({
        path: '/account/profile',
        query: { redirect: route.fullPath }
      });
    emits('close'); 
    }else{
      setTimeout(() => emits('success'), 100);
    }

    gtag('event', 'cta_event', {
      action: 'click',
      cta_name: 'submit otp',
    });
  } catch (err) {
    console.log("error", err);
    error.value = 'Invalid OTP';
  } finally {
    isChecking.value = false;
  }
};

const reset = () => {
  otp.value = null;
  isOTPSent.value = false;
  error.value = '';
};

async function onSubmit(event: FormSubmitEvent<Schema>) {
  isLoading.value = true;
  const { name, mobile } = event.data;
  await sendOTP(name, mobile.toString());
  gtag('event', 'cta_event', {
    action: 'click',
    cta_name: 'get otp',
    username: name,
    mobile: mobile.toString()
  });
}

const runtimeConfig = useRuntimeConfig();

const emits = defineEmits(['success']);
const isLoading = ref(false);
const isOTPSent = ref(false);
const isChecking = ref(false);
const error = ref('');
const otp = ref<string | null>(null);

const masked = computed(() => `+91 xxxxxxxx${state?.mobile.toString().substring(8, 10)}`);


const startTimer = () => {
  resendDisabled.value = true;
  timer.value = 30;
  const interval = setInterval(() => {
    if (timer.value > 0) {
      timer.value--;
    } else {
      resendDisabled.value = false;
      clearInterval(interval);
    }
  }, 1000);
};

const resendOTP = async () => {
  if (resendDisabled.value || resendCount.value >= maxResendAttempts) {
    error.value = resendCount.value >= maxResendAttempts
      ? 'Maximum resend attempts reached. Please try again later.'
      : '';
    return;
  }

  error.value = '';
  isLoading.value = true;
  try {
    await sendOTP(state.name, state.mobile.toString());
    resendCount.value++; // Increment the counter after a successful attempt
    startTimer();
  } catch (err) {
    error.value = 'Failed to resend OTP. Please try again later.';
  } finally {
    isLoading.value = false;
  }
};

// Call startTimer when OTP is sent for the first time
const sendOTP = async (name: string, phone: string) => {
  error.value = '';
  try {
    await isNewUser(phone);
    const { error } = await useAPIHandler('post', '/auth/otp', {
      body: { partner: runtimeConfig.public.partnerCode, name, phone }
    });
    if (error) {
      throw new Error();
    }
    isOTPSent.value = true;
    startTimer(); // Start the timer on OTP send
  } catch (err) {
    error.value = 'Failed to send OTP. Please try after sometime.';
  } finally {
    isLoading.value = false;
  }
};



</script>
